import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { Rootstate } from '../../store';
import { GRID_SIZE } from './Grid';
import { setFog } from '../../reducers/map/mapReducerActions';
import { updatePartialMapOnServer } from '../../utils/updatePartialMapOnServer';

const FogRenderer = ({ transparent }: { transparent?: boolean }) => {
    const fog = useSelector((state: Rootstate) => state.map.map?.fog, shallowEqual);
    const fogShape = useSelector((state: Rootstate) => state.map.fogShape, shallowEqual);
    const { xTiles, yTiles, mapId } = useSelector((state: Rootstate) => ({
        xTiles: state.grid.xTiles,
        yTiles: state.grid.yTiles,
        mapId: state.map.mapId,
    }), shallowEqual);
    const imageDataRef = useRef<Uint8ClampedArray>();
    const offScreenCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const fogRef = useRef(cloneDeep(fog));

    const dispatch = useDispatch();

    const drawCallback = useCallback((event: Event) => {
        if (!fogShape) {
            return;
        }
        const { x, y } = (event as CustomEvent).detail as { x: number, y: number };
        let newFog = fogRef.current as boolean[][];
        if (!fogRef.current || fogRef.current.length !== yTiles * 10) {
            newFog = [];
            for (let y = 0; y < yTiles * 10; y++) {
                const row: boolean[] = [];
                for (let x = 0; x < xTiles * 10; x++) {
                    row.push(false);
                }
                newFog.push(row);
            }
            fogRef.current = newFog;
        }

        const halfSize = Math.floor(fogShape.size);
        const roundedX = Math.round(x / 2.5);
        const roundedY = Math.round(y / 2.5);

        for (let xOffset = -halfSize; xOffset < halfSize; xOffset++) {
            for (let yOffset = -halfSize; yOffset < halfSize; yOffset++) {
                if (roundedX + xOffset < 0 || roundedX + xOffset >= newFog[0].length) {
                    continue;
                }
                if (roundedY + yOffset < 0 || roundedY + yOffset >= newFog.length) {
                    continue;
                }
                if (fogShape.type === 'sphere' && Math.sqrt(yOffset*yOffset + xOffset*xOffset) > halfSize) {
                    continue;
                }
                if (fogShape.add) {
                    newFog[roundedY + yOffset][roundedX + xOffset] = true;
                } else {
                    newFog[roundedY + yOffset][roundedX + xOffset] = false;
                }
            }
        }
        redrawFog();
        updateFog(fogRef.current);
    }, [fogShape]);

    useEffect(() => {
        fogRef.current = cloneDeep(fog);
        redrawFog();
    }, [fog]);

    const updateFog = useMemo(() => {
        return debounce((fog: boolean[][]) => {
            const clonedFog = cloneDeep(fog);
            dispatch(setFog(clonedFog));
            updatePartialMapOnServer({ fog: clonedFog }, mapId);
            fogRef.current = cloneDeep(clonedFog);
        }, 500);
    }, []);

    useEffect(() => {
        const callback = drawCallback;
        if (fogShape) {
            document.addEventListener("custom-move", callback);
        }
        return () => {
            document.removeEventListener("custom-move", callback);
        };
    }, [drawCallback]);

    const redrawFog = () => {
        if (!fogRef.current || fogRef.current.length === 0 || fogRef.current[0].length === 0) {
            if(canvasRef.current) {
                const context = canvasRef.current.getContext('2d');
                context?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            }
            return;
        }

        const width = fogRef.current[0].length;
        const height = fogRef.current.length;

        if (!imageDataRef.current || imageDataRef.current.length !== width * height * 4) {
            imageDataRef.current = new Uint8ClampedArray(width * height * 4);
        }

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                // const neighbourCount = getNeighbourCount(fogRef.current, x, y);
                // const opacity = neighbourCount < 3
                //     ? 127
                //     : 255;
                const color = fogRef.current[y][x] ? [30, 30, 30, 255] : [0, 0, 0, 0];
                const index = (y * width + x) * 4;
                imageDataRef.current[index] = color[0];
                imageDataRef.current[index + 1] = color[1];
                imageDataRef.current[index + 2] = color[2];
                imageDataRef.current[index + 3] = color[3];
            }
        }

        const offScreenCanvas = offScreenCanvasRef.current;
        offScreenCanvas.width = width;
        offScreenCanvas.height = height;
        const offScreenCtx = offScreenCanvas.getContext('2d');
        if (offScreenCtx) {
            const imageData = new ImageData(imageDataRef.current, width, height);
            offScreenCtx.putImageData(imageData, 0, 0);
        }

        if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                ctx.drawImage(offScreenCanvas, 0, 0, width, height, 0, 0, xTiles * GRID_SIZE, yTiles * GRID_SIZE);
            }
        }
    };

    return (
        <div>
            <canvas
                ref={canvasRef}
                width={xTiles * 25}
                height={yTiles * 25}
                style={{
                    position: 'absolute',
                    width: `${xTiles * GRID_SIZE}px`,
                    height: `${yTiles * GRID_SIZE}px`,
                    imageRendering: 'pixelated',
                    opacity: transparent ? '70%' : '100%',
                    pointerEvents: 'none',
                }}
            />
        </div>
    );
};

export default FogRenderer;
